// src/components/Billing/BillingList.jsx
import React, { useMemo, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Typography, Button, Box, FormControlLabel, Tooltip, Chip,
  Stack, Divider, Menu, MenuItem, IconButton, TablePagination, FormControl,
  InputLabel, Select,
} from "@mui/material";
import ViewColumnIcon        from "@mui/icons-material/ViewColumn";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import FileDownloadIcon      from "@mui/icons-material/FileDownload";
import RefreshIcon           from "@mui/icons-material/Refresh";
import AddIcon               from "@mui/icons-material/Add";
import CheckIcon             from "@mui/icons-material/Check";
import TableChartIcon        from "@mui/icons-material/TableChart";
import GridOnIcon            from "@mui/icons-material/GridOn";
import PictureAsPdfIcon      from "@mui/icons-material/PictureAsPdf";
import AccountBalanceIcon    from "@mui/icons-material/AccountBalance";
import OpenInNewIcon         from "@mui/icons-material/OpenInNew";
import EditIcon              from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { UITableShell, UITableToolbar } from "../../../components/ui";
import LoadingComponent      from "../../ComponentLoader";
import { updateBill }        from "../../../services/billing";
import {
  getBillInvoiceNumberDisplay,
} from "../../../utils/billInternalDecoration";
import { BILL_TYPES, filterBillsByParty, getOtherServiceProviders } from "../../../utils/billParty";
import {
  buildBillingExportRow,
  getBillingSummaryCounts,
  getDisplayBillParty,
  getDisplayPoAmount,
  getDisplayPoNumber,
  getDisplayPoParty,
  getDisplayPoStatus,
  hasAssociatedPurchaseOrder,
  isAllBillsReceivedApplicable,
  resolveBillingPoContext,
} from "../billingTableRows";


const BILLCOLUMNS = [
  { id: "poNumber",    label: "PO #",             minWidth: 160, align: "left",   visible: true  },
  { id: "poStatus",    label: "PO Status",         minWidth: 140, align: "left",   visible: true  },
  { id: "poParty",     label: "PO Party",          minWidth: 160, align: "left",   visible: true  },
  { id: "poAmount",    label: "PO Amount",         minWidth: 120, align: "left",   visible: true  },
  { id: "billId",      label: "Invoice #",         minWidth: 140, align: "left",   visible: true  },
  { id: "billParty",   label: "Bill Party",        minWidth: 160, align: "left",   visible: true  },
  { id: "received",    label: "All Bills Received",minWidth: 150, align: "center", visible: true  },
  { id: "amount",      label: "Subtotal",          minWidth: 120, align: "left",   visible: true  },
  { id: "action",      label: "Action",            minWidth: 110, align: "center", visible: true, locked: true },
];


const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export const buildBillingFooterMetrics = ({
  poTarget = 0,
  financialSummary = {},
  currencyCode = "CAD",
} = {}) => {
  const normalizedCurrency = String(currencyCode || "CAD").toUpperCase();
  const formatMoney = (value) => {
    const formatted = new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);
    return normalizedCurrency === "CAD" && formatted.startsWith("$")
      ? `CA${formatted}`
      : formatted;
  };
  const margin = financialSummary?.billedMarginPercentage == null
    ? null
    : Number(financialSummary.billedMarginPercentage);

  return [
    ["PO Target", formatMoney(poTarget)],
    ["Bills", formatMoney(financialSummary?.totalBills)],
    ["Billed Margin Amount", formatMoney(financialSummary?.billedMarginAmount)],
    ["Billed Margin", Number.isFinite(margin) ? `${margin.toFixed(2)}%` : "—"],
    ["Sales Subtotal", formatMoney(financialSummary?.salesSubtotal)],
  ];
};

const getBillStatusColor = (str) => {
  const s = String(str || "").toLowerCase();
  if (s.includes("billed") || s.includes("closed") || s.includes("complete") || s.includes("received")) {
    return {
      bg: "var(--ec-status-success-bg)",
      text: "var(--ec-status-success-text)",
      border: "var(--ec-status-success-border)",
    };
  }
  if (s.includes("ready") || s.includes("sent") || s.includes("production") || s.includes("approved")) {
    return {
      bg: "var(--ec-status-info-bg)",
      text: "var(--ec-status-info-text)",
      border: "var(--ec-status-info-border)",
    };
  }
  if (s.includes("pending") || s.includes("created") || s.includes("draft") || s.includes("requested")) {
    return {
      bg: "var(--ec-status-warning-bg)",
      text: "var(--ec-status-warning-text)",
      border: "var(--ec-status-warning-border)",
    };
  }
  if (s.includes("cancelled") || s.includes("failed") || s.includes("declined")) {
    return {
      bg: "var(--ec-status-error-bg)",
      text: "var(--ec-status-error-text)",
      border: "var(--ec-status-error-border)",
    };
  }
  return {
    bg: "var(--ec-status-neutral-bg)",
    text: "var(--ec-status-neutral-text)",
    border: "var(--ec-status-neutral-border)",
  };
};

const StatusChip = ({ label }) => {
  if (!label) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        —
      </Typography>
    );
  }

  const sc = getBillStatusColor(label);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        bgcolor: sc.bg,
        color: sc.text,
        border: `1px solid ${sc.border}`,
      }}
    >
      {label}
    </Box>
  );
};

export const isPoGroupAllBillsReceived = (bills = [], poAllBillsReceivedStatus = {}) => {
  if (!bills?.length) return false;
  return bills.every(
    (bill) => !!(poAllBillsReceivedStatus?.[bill.id] ?? bill.allBillsReceived)
  );
};

const BillingList = ({
  onBillClick,
  setPoAllBillsReceivedStatus,
  groupedBills,
  getPurchaseOrderHref,
  poAllBillsReceivedStatus,
  fullBillAmount,
  loading,
  fetchData,
  // Toolbar action
  onAddBill,
  onAddBillFromPo,
  financialSummary = {},
  currencyCode = "CAD",
}) => {

  const [densityMode,   setDensityMode]   = useState("comfortable");
  const [densityAnchor, setDensityAnchor] = useState(null);
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const [exportAnchor,  setExportAnchor]  = useState(null);
  const [page,          setPage]          = useState(0);
  const [rowsPerPage,   setRowsPerPage]   = useState(25);
  const [billType, setBillType] = useState(BILL_TYPES.ALL);
  const [serviceProvider, setServiceProvider] = useState("");
  const [visibleCols,   setVisibleCols]   = useState(
    () => new Set(BILLCOLUMNS.filter(c => c.visible !== false).map(c => c.id))
  );

 
  const densitySettings = {
    compact:     { py: 0.75, fontSize: "0.8125rem" },
    comfortable: { py: 1.5,  fontSize: "0.875rem"  },
    spacious:    { py: 2.25, fontSize: "0.9375rem" },
  };
  const density = densitySettings[densityMode];
 
  const toggleCol = (id) => {
    const col = BILLCOLUMNS.find(c => c.id === id);
    if (col?.locked) return;
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size <= 2) return prev; next.delete(id); }
      else next.add(id);
      return next;
    });
  };

 
  const getBillSubtotal = (bill) => {
    if (bill?.subTotal !== null && bill?.subTotal !== undefined) {
      const subtotal = Number(bill.subTotal);
      if (Number.isFinite(subtotal)) return subtotal;
    }
    const amount = Number(bill?.amount ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const safeFullBillAmount = Number(fullBillAmount) || 0;
  const footerMetrics = buildBillingFooterMetrics({
    poTarget: safeFullBillAmount,
    financialSummary,
    currencyCode,
  });
 
  const groupCompleteByPo = useMemo(() => {
    const map = {};
    Object.entries(groupedBills || {}).forEach(([po, bills]) => {
      const isPoBackedGroup = (bills || []).some((bill) =>
        hasAssociatedPurchaseOrder({ po, bill })
      );
      map[po] = isPoBackedGroup && isPoGroupAllBillsReceived(bills, poAllBillsReceivedStatus);
    });
    return map;
  }, [groupedBills, poAllBillsReceivedStatus]);

 
  const allRows = useMemo(() => {
    const out = [];
    Object.entries(groupedBills || {}).forEach(([po, bills]) => {
      (bills || []).forEach((bill) => { out.push({ po, bill }); });
    });
    return out;
  }, [groupedBills]);
  const serviceProviders = useMemo(() => getOtherServiceProviders(allRows), [allRows]);
  const rows = useMemo(() => filterBillsByParty(allRows, billType, serviceProvider),
    [allRows, billType, serviceProvider]);
  const poTotals = useMemo(() => {
    const summary = getBillingSummaryCounts(rows, groupCompleteByPo);
    return { done: summary.poDone, total: summary.poTotal };
  }, [rows, groupCompleteByPo]);

 
  const handleAllBillsReceivedChangeForGroup = async (po, bills, isChecked) => {
    setPoAllBillsReceivedStatus((prev) => {
      const next = { ...prev };
      (bills || []).forEach((b) => { next[b.id] = isChecked; });
      return next;
    });
    try {
      await Promise.all(
        (bills || []).map((b) => updateBill(b.id, { allBillsReceived: isChecked }))
      );
    } finally {
      fetchData();
    }
  };

 
  const guardedOpenBill = (po, bill, openAction) => {
    if (!!groupCompleteByPo[po]) {
      const proceed = window.confirm(
        "All Bills Received is marked for this PO. Open the bill anyway?"
      );
      if (!proceed) return;
    }
    openAction();
  };

 
  const exportRows = rows.map((row) => buildBillingExportRow(row, {
    groupComplete: groupCompleteByPo[row.po],
    moneyFormatter: money,
    getInvoiceDisplay: getBillInvoiceNumberDisplay,
    getBillSubtotal,
  }));

  const handleExportCSV = () => {
    if (!exportRows.length) return;
    const headers = Object.keys(exportRows[0]);
    const csv = [
      headers.join(","),
      ...exportRows.map(r => headers.map(h => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    a.download = "bills.csv";
    a.click();
  };

  const handleExportExcel = () => {
    if (!exportRows.length) return;
    import("xlsx").then(XLSX => {
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bills");
      XLSX.writeFile(wb, "bills.xlsx");
    });
  };

  const handleExportPDF = () => {
    if (!exportRows.length) return;
    import("jspdf").then(({ jsPDF }) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14); doc.text("Bills", 14, 15);
        doc.setFontSize(9);  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 22);
        doc.autoTable({
          startY: 28,
          head: [Object.keys(exportRows[0])],
          body: exportRows.map(r => Object.values(r)),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
        doc.save("bills.pdf");
      });
    });
  };

 
  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

 
  const summaryCounts = getBillingSummaryCounts(rows, groupCompleteByPo);
  const totalBills = summaryCounts.totalBills;
  const totalPOs   = summaryCounts.totalPOs;
 
  if (loading) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 4 }}>
        <LoadingComponent />
      </Box>
    );
  }
 
  return (
    <>
      <UITableShell
        customLayout
        paperSx={{
          borderRadius: "10px",
          border: "1px solid",
          borderColor: "divider",
          borderTop: "3px solid",
          borderTopColor: "primary.main",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
          display: "flex",
          flexDirection: "column",
          mt: 0,
        }}
      >
 
        <UITableToolbar sx={{ backgroundColor: "background.paper", p: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{
              px: 2, pt: 1.25, pb: 1.25,
              borderBottom: "1px solid", borderColor: "divider",
            }}
          >
       
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                Bills
              </Typography> */}

              <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
  Bills
</Typography>
<Tooltip
  title="PO-backed bills are grouped by PO Number. Standalone bills are listed separately and do not affect PO completion."
  arrow
  placement="right"
>
  <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.disabled", cursor: "help", mt: "1px" }} />
</Tooltip>
              {totalPOs > 0 && (
                <Chip
                  label={`${totalPOs} PO${totalPOs !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: "0.72rem", height: 22,
                    bgcolor: "var(--ec-status-info-bg)",
                    color: "var(--ec-status-info-text)",
                    border: "1px solid var(--ec-status-info-border)",
                    "& .MuiChip-label": { px: "8px" },
                  }}
                />
              )}
              {totalBills > 0 && (
                <Chip
                  label={`${totalBills} bill${totalBills !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: "0.72rem", height: 22,
                    bgcolor: "var(--ec-status-neutral-bg)",
                    color: "var(--ec-status-neutral-text)",
                    border: "1px solid var(--ec-status-neutral-border)",
                    "& .MuiChip-label": { px: "8px" },
                  }}
                />
              )}
              {poTotals.total > 0 && (
                <Chip
                  label={`${poTotals.done}/${poTotals.total} complete`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: "0.72rem", height: 22,
                    bgcolor: poTotals.done === poTotals.total
                      ? "var(--ec-status-success-bg)"
                      : "var(--ec-status-warning-bg)",
                    color: poTotals.done === poTotals.total
                      ? "var(--ec-status-success-text)"
                      : "var(--ec-status-warning-text)",
                    border: `1px solid ${poTotals.done === poTotals.total
                      ? "var(--ec-status-success-border)"
                      : "var(--ec-status-warning-border)"}`,
                    "& .MuiChip-label": { px: "8px" },
                  }}
                />
              )}
            </Stack>

    
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="billing-list-bill-type-filter">Bill Type</InputLabel>
                <Select
                  labelId="billing-list-bill-type-filter"
                  label="Bill Type"
                  value={billType}
                  onChange={(event) => {
                    setBillType(event.target.value);
                    if (event.target.value !== BILL_TYPES.OTHER_SERVICE) setServiceProvider("");
                    setPage(0);
                  }}
                >
                  <MenuItem value={BILL_TYPES.ALL}>All</MenuItem>
                  <MenuItem value={BILL_TYPES.SUPPLIER}>Suppliers</MenuItem>
                  <MenuItem value={BILL_TYPES.DECORATOR}>Decorators</MenuItem>
                  <MenuItem value={BILL_TYPES.SHIPPING_ACCOUNT}>Shipping</MenuItem>
                  <MenuItem value={BILL_TYPES.OTHER_SERVICE}>Other Services</MenuItem>
                  <MenuItem value={BILL_TYPES.INTERNAL_DECORATION}>Internal Decorations</MenuItem>
                </Select>
              </FormControl>
              {billType === BILL_TYPES.OTHER_SERVICE && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="billing-list-provider-filter">Service Provider</InputLabel>
                  <Select labelId="billing-list-provider-filter" label="Service Provider" value={serviceProvider}
                    onChange={(event) => { setServiceProvider(event.target.value); setPage(0); }}>
                    <MenuItem value="">All providers</MenuItem>
                    {serviceProviders.map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>{provider.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
             
              <Tooltip title="Show/hide columns" arrow>
                <IconButton size="small" onClick={e => setColMenuAnchor(e.currentTarget)} sx={{ color: "text.secondary" }}>
                  <ViewColumnIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Row density" arrow>
                <IconButton size="small" onClick={e => setDensityAnchor(e.currentTarget)} sx={{ color: "text.secondary" }}>
                  <FormatLineSpacingIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export" arrow>
                <IconButton size="small" onClick={e => setExportAnchor(e.currentTarget)} sx={{ color: "text.secondary" }}>
                  <FileDownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {typeof fetchData === "function" && (
                <Tooltip title="Refresh" arrow>
                  <IconButton size="small" onClick={fetchData} sx={{ color: "text.secondary" }}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />

               {typeof onAddBill === "function" && (
                <Tooltip title="Add a new bill" arrow>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={onAddBill}
                    sx={{
                      textTransform: "none", fontWeight: 700,
                      fontSize: "0.8rem", height: 32, borderRadius: "8px",
                      boxShadow: "none", whiteSpace: "nowrap", minWidth: 110,
                    }}
                  >
                    Add Bill
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </UITableToolbar>

 
        <TableContainer
          sx={{
            flex: 1, overflow: "auto", minHeight: 280, maxHeight: 520,
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(148,163,184,0.8)", borderRadius: 99 },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {BILLCOLUMNS.filter(c => visibleCols.has(c.id)).map(col => (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={{
                      minWidth: col.minWidth,
                      px: 2, py: 1.25,
                      backgroundColor: "#1e293b !important",
                      color: "#f8fafc !important",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                      position: "sticky", top: 0, zIndex: 2,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                "& .MuiTableCell-root": { borderBottom: "1px solid", borderColor: "divider" },
                "& .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={BILLCOLUMNS.length} sx={{ border: "none", p: 0 }}>
                    <Box sx={{
                      display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", py: 8, color: "text.disabled",
                    }}>
                      <AccountBalanceIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: "text.secondary" }}>
                        No bills yet
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.disabled", mb: 2 }}>
                        Bills will appear here once purchase orders are created.
                      </Typography>
                      {typeof onAddBill === "function" && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                          onClick={onAddBill}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                        >
                          Add Bill
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map(({ po, bill }, idx) => {
                  const row = { po, bill };
                  const displayAmount = getBillSubtotal(bill);
                  const groupBills    = groupedBills?.[po] || [];
                  const groupComplete = !!groupCompleteByPo[po];
                  const hasBill       = !!bill?.id;
                  const hasPo         = hasAssociatedPurchaseOrder(row);
                  const poNumberDisplay = getDisplayPoNumber(row);
                  const poStatusDisplay = getDisplayPoStatus(row);
                  const poPartyDisplay = getDisplayPoParty(row);
                  const poAmountDisplay = getDisplayPoAmount(row, money);
                  const billPartyDisplay = getDisplayBillParty(bill);
                  const allReceivedApplicable = isAllBillsReceivedApplicable(row);
                  const poContext = resolveBillingPoContext(row);

           
                  const prevPo = idx > 0 ? pagedRows[idx - 1].po : null;
                  const isFirstInGroup = po !== prevPo;

                  return (
                    <TableRow
                      key={`${po}-${bill.id || idx}`}
                      hover
                      sx={{
                        cursor: "default",
                        bgcolor: groupComplete
                          ? "var(--ec-status-success-bg)"
                          : idx % 2 === 0 ? "background.paper" : "#f8fafc",
                        transition: "background-color 0.12s",
          
                        ...(isFirstInGroup && idx > 0 && {
                          "& .MuiTableCell-root": {
                            borderTop: "2px solid",
                            borderTopColor: "divider",
                          },
                        }),
                      }}
                    >
               
                      {visibleCols.has("poNumber") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                            {hasPo && getPurchaseOrderHref && bill?._poId ? (
                              <Box
                                component="a"
                                href={getPurchaseOrderHref(bill)}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                  fontFamily: "monospace", fontWeight: 700,
                                  fontSize: density.fontSize, color: "primary.main",
                                  textDecoration: "underline",
                                  display: "flex", alignItems: "center", gap: 0.5,
                                }}
                              >
                                {poNumberDisplay}
                                <OpenInNewIcon sx={{ fontSize: 11 }} />
                              </Box>
                            ) : (
                              <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: density.fontSize }}>
                                {poNumberDisplay}
                              </Typography>
                            )}
                            {hasPo && groupComplete && (
                              <Chip
                                size="small"
                                label="Complete"
                                sx={{
                                  height: 18, fontSize: "0.65rem", fontWeight: 700,
                                  bgcolor: "var(--ec-status-success-bg)",
                                  color: "var(--ec-status-success-text)",
                                  border: "1px solid var(--ec-status-success-border)",
                                  "& .MuiChip-label": { px: "6px" },
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      )}
 
                      {visibleCols.has("poStatus") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <StatusChip label={poStatusDisplay} />
                        </TableCell>
                      )}

              
                      {visibleCols.has("poParty") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Typography variant="body2" sx={{ fontSize: density.fontSize }}>
                            {poPartyDisplay}
                          </Typography>
                        </TableCell>
                      )}

               
                      {visibleCols.has("poAmount") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: density.fontSize }}>
                            {poAmountDisplay}
                          </Typography>
                        </TableCell>
                      )}
 
                      {visibleCols.has("billId") && (
                        <TableCell
                          sx={{
                            px: 2, py: density.py,
                            cursor: hasBill ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (!hasBill) return;
                            guardedOpenBill(po, bill, () => {
                              onBillClick(bill.id);
                            });
                          }}
                        >
                          {(() => {
                            const display = getBillInvoiceNumberDisplay(bill);
                            const content = <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace", fontWeight: 600,
                              fontSize: density.fontSize,
                              color: display.missing ? "error.main" : display.notApplicable ? "text.secondary" : hasBill ? "primary.main" : "text.disabled",
                              textDecoration: hasBill ? "underline" : "none",
                            }}
                          >
                              {display.label}
                            </Typography>;
                            return display.notApplicable
                              ? <Tooltip title="Invoice number not required for internal decoration">{content}</Tooltip>
                              : content;
                          })()}
                        </TableCell>
                      )}

          
                      {visibleCols.has("billParty") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Typography variant="body2" sx={{ fontSize: density.fontSize }}>
                            {billPartyDisplay}
                          </Typography>
                        </TableCell>
                      )}
 
{visibleCols.has("received") && (
  <TableCell align="center" sx={{ px: 2, py: density.py }}>
    {allReceivedApplicable ? (
      <Tooltip
        title="Mark this PO as having every vendor bill received/entered. Applies to all bills under this PO."
        arrow
      >
      <span>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={groupComplete}
              disabled={!allReceivedApplicable}
              onChange={(e) =>
                handleAllBillsReceivedChangeForGroup(
                  po,
                  groupBills,
                  e.target.checked
                )
              }
              sx={{
                color: "var(--ec-status-success-border)",
                "&.Mui-checked": { color: "success.main" },
                "&.Mui-disabled": { opacity: 0.35 },
                p: 0.5,
              }}
            />
          }
          label=""
          sx={{ m: 0 }}
        />
      </span>
      </Tooltip>
    ) : (
      <Tooltip
        title="Not applicable — this bill is not associated with a purchase order."
        arrow
      >
        <Typography component="span" variant="body2" sx={{ color: "text.disabled" }}>
          —
        </Typography>
      </Tooltip>
    )}
  </TableCell>
)}

               
                      {visibleCols.has("amount") && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700, fontSize: density.fontSize,
                              color: hasBill ? "text.primary" : "text.disabled",
                            }}
                          >
                            {hasBill ? `CA$${money(displayAmount)}` : "—"}
                          </Typography>
                        </TableCell>
                      )}

              
                      {visibleCols.has("action") && (
                        <TableCell align="center" sx={{ px: 2, py: density.py }}>
                          <Tooltip
                            title={
                              groupComplete
                                ? "PO is marked complete — click to edit anyway"
                                : hasBill ? "Edit bill" : "Add bill for this PO"
                            }
                            arrow
                          >
                            <Button
                              size="small"
                              variant={hasBill ? "outlined" : "contained"}
                              startIcon={hasBill
                                ? <EditIcon sx={{ fontSize: 13 }} />
                                : <AddIcon sx={{ fontSize: 13 }} />
                              }
                              onClick={() => {
                                if (hasBill) {
                                  guardedOpenBill(po, bill, () => {
                                    onBillClick(bill.id);
                                  });
                                  return;
                                }
                                const seededShippingAccountId =
                                  bill?._poSupplierInfo?.shippingAccountId ??
                                  bill?._poSupplierInfo?.shippingAccount?.id ??
                                  null;
                                const seededShippingAccountName =
                                  bill?._poSupplierInfo?.shippingAccount?.accountName ||
                                  bill?._poSupplierInfo?.shippingAccount?.name ||
                                  "";
                                const poSeedData = {
                                  poId: bill?._poId,
                                  poNumber: bill?.poNumber,
                                  poType: bill?._poType || bill?.poType || "",
                                  supplierId:
                                    bill?._poSupplierInfo?.supplierId ??
                                    bill?._poSupplierInfo?.vendorSupplierId ??
                                    bill?.supplierId ??
                                    null,
                                  decoratorId:
                                    bill?._poSupplierInfo?.decoratorId ??
                                    bill?.decoratorId ??
                                    null,
                                  shippingAccountId: seededShippingAccountId,
                                  shippingAccount: seededShippingAccountId
                                    ? {
                                        id: seededShippingAccountId,
                                        accountName: seededShippingAccountName,
                                      }
                                    : null,
                                  billPartyType:
                                    bill?._poSupplierInfo?.party?.partyType === "other-service"
                                      ? "other-service"
                                      : undefined,
                                  billPartySourceType:
                                    bill?._poSupplierInfo?.party?.partyType === "other-service"
                                      ? "other-service"
                                      : undefined,
                                  supplierName: bill?.supplierInfo || bill?.billSupplierInfo?.Name || "",
                                  decoratorName:
                                    bill?._poSupplierInfo?.decoratorName ||
                                    bill?._poSupplierInfo?.decoratorTitle ||
                                    bill?.billDecoratorInfo?.title ||
                                    "",
                                  items: Array.isArray(bill?._poItems) ? bill._poItems : [],
                                  decoratorInfo: bill?._poSupplierInfo && typeof bill._poSupplierInfo === "object"
                                    ? {
                                        id: bill?._poSupplierInfo?.decoratorId ?? null,
                                        title: bill?._poSupplierInfo?.decoratorTitle || bill?._poSupplierInfo?.decoratorName || "",
                                      }
                                    : null,
                                  supplierInfo: bill?._poSupplierInfo && typeof bill._poSupplierInfo === "object"
                                    ? bill._poSupplierInfo : {},
                                  poPartyName:
                                    seededShippingAccountName ||
                                    bill?._poSupplierInfo?.decoratorName ||
                                    bill?._poSupplierInfo?.decoratorTitle ||
                                    bill?._poSupplierInfo?.supplierName ||
                                    bill?.supplierInfo ||
                                    "",
                                  _poItems: Array.isArray(bill?._poItems) ? bill._poItems : [],
                                };
                                onAddBillFromPo({
                                  poNumber: bill.poNumber,
                                  poStatus: bill.poStatus,
                                  poAmount: poContext.poTarget ?? bill.poAmount,
                                  poSeedData,
                                });
                              }}
                              sx={{
                                textTransform: "none", fontWeight: 700,
                                fontSize: "0.78rem", height: 28, borderRadius: "8px",
                                boxShadow: "none", whiteSpace: "nowrap",
                                ...(hasBill ? {} : {
                                  bgcolor: "primary.main", color: "#fff",
                                  "&:hover": { bgcolor: "primary.dark" },
                                }),
                              }}
                            >
                              {hasBill ? "Edit" : "Add Bill"}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

<Box sx={{
  display: "flex", alignItems: "center", justifyContent: "space-between",
  flexWrap: "wrap", gap: 1,
  px: 2, py: 0.75,
  borderTop: "1px solid", borderColor: "divider",
  bgcolor: "background.paper", flexShrink: 0,
}}>

  <Stack
    data-testid="billing-table-footer-metrics"
    direction="row"
    alignItems="center"
    spacing={1.5}
    useFlexGap
    flexWrap="wrap"
    sx={{ minWidth: 0, flex: "1 1 680px" }}
  >
    {footerMetrics.map(([label, value], index) => (
      <React.Fragment key={label}>
        {index > 0 && (
          <Box sx={{ width: "1px", height: 14, bgcolor: "divider", flexShrink: 0 }} />
        )}
        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{label}: </Box>
          <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {value}
          </Box>
        </Typography>
      </React.Fragment>
    ))}

    <Box sx={{ width: "1px", height: 14, bgcolor: "divider", flexShrink: 0 }} />

    <Typography variant="caption" sx={{ color: "text.disabled", whiteSpace: "nowrap" }}>
      {rows.length > 0
        ? `Showing ${page * rowsPerPage + 1}–${Math.min(rows.length, page * rowsPerPage + rowsPerPage)} of ${rows.length} row${rows.length !== 1 ? "s" : ""}`
        : "No rows"
      }
    </Typography>
  </Stack>

  <TablePagination
    component="div"
    count={rows.length}
    page={page}
    onPageChange={(_, newPage) => setPage(newPage)}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
    rowsPerPageOptions={[10, 25, 50]}
    sx={{
      border: 0,
      "& .MuiTablePagination-toolbar": { minHeight: 40, px: 0, alignItems: "center" },
      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
        fontSize: "0.78rem", color: "text.secondary", mb: 0, lineHeight: 1,
      },
      "& .MuiTablePagination-select": { fontSize: "0.78rem" },
    }}
  />
</Box>
      </UITableShell>

     
      <Menu
        anchorEl={colMenuAnchor} open={Boolean(colMenuAnchor)}
        onClose={() => setColMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: "block", color: "text.disabled" }}>
          Columns
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {BILLCOLUMNS.filter(c => !c.locked).map(col => (
          <MenuItem key={col.id} onClick={() => toggleCol(col.id)} sx={{ py: 0.4 }}>
            <Checkbox size="small" checked={visibleCols.has(col.id)} sx={{ mr: 1, p: 0.25 }} />
            {col.label}
          </MenuItem>
        ))}
      </Menu>
 
      <Menu
        anchorEl={densityAnchor} open={Boolean(densityAnchor)}
        onClose={() => setDensityAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 160, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: "block", color: "text.disabled" }}>
          Row Density
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {[
          { key: "compact",     label: "Compact"     },
          { key: "comfortable", label: "Comfortable" },
          { key: "spacious",    label: "Spacious"    },
        ].map(opt => (
          <MenuItem
            key={opt.key}
            selected={densityMode === opt.key}
            onClick={() => { setDensityMode(opt.key); setDensityAnchor(null); }}
            sx={{ py: 0.75, fontSize: "0.875rem" }}
          >
            {densityMode === opt.key
              ? <CheckIcon sx={{ fontSize: 15, mr: 1, color: "primary.main" }} />
              : <Box sx={{ width: 15, mr: 1 }} />
            }
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

 
      <Menu
        anchorEl={exportAnchor} open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: "block", color: "text.disabled" }}>
          Export As
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        <MenuItem onClick={() => { handleExportCSV(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: "0.875rem", gap: 1 }}>
          <TableChartIcon sx={{ fontSize: 16, color: "text.secondary" }} /> CSV
        </MenuItem>
        <MenuItem onClick={() => { handleExportExcel(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: "0.875rem", gap: 1 }}>
          <GridOnIcon sx={{ fontSize: 16, color: "success.main" }} /> Excel
        </MenuItem>
        <MenuItem onClick={() => { handleExportPDF(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: "0.875rem", gap: 1 }}>
          <PictureAsPdfIcon sx={{ fontSize: 16, color: "error.main" }} /> PDF
        </MenuItem>
      </Menu>
    </>
  );
};

export default BillingList;
